/* eslint-disable no-restricted-properties -- sanctioned home for the react-dom compat shims */
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";

// React 19 removed APIs that antd v4 / rc-* internals still rely on. We re-attach
// them onto the `react-dom` namespace at runtime (not a patch). Import for side
// effect before any antd rendering. Remove once antd is on v5+.
//
// - findDOMNode: used by rc-motion/rc-trigger/rc-select for positioning.
// - createRoot: rc-util's render helper picks the modern (createRoot) path only
//   if `react-dom` exposes it at the top level, which React 19 no longer does
//   (it moved to react-dom/client). Without it rc-util falls back to the removed
//   `ReactDOM.render` and every antd portal (notification/message/Modal) throws.
//
// findDOMNode below is a 1:1 port of React's own implementation, which React 19
// still ships but no longer exports. Sources: ReactFiberTreeReflection.js and
// ReactFiberReconciler.js in facebook/react, cross-checked against the compiled
// react-dom 19.2.7 (cjs/react-dom-client.development.js). Only the dev-only
// StrictMode/refs-in-render warnings are left out. Keep it 1:1 with upstream --
// re-check the tag and flag constants below on every React bump.

// Fiber tags and effect flags, verified against react-dom 19.2.7.
const HOST_ROOT = 3;
const HOST_COMPONENT = 5;
const HOST_TEXT = 6;
const HOST_HOISTABLE = 26;
const HOST_SINGLETON = 27;
const PLACEMENT = 2;
const HYDRATING = 4096;
const ELEMENT_NODE = 1;

interface Fiber {
    tag: number;
    flags: number;
    stateNode: unknown;
    child: Fiber | null;
    sibling: Fiber | null;
    return: Fiber | null;
    alternate: Fiber | null;
}

interface ReactComponentInternals {
    _reactInternals?: Fiber;
}

function getNearestMountedFiber(fiber: Fiber): Fiber | null {
    let node = fiber;
    let nearestMounted: Fiber | null = fiber;
    if (!fiber.alternate) {
        // No alternate means this may be a not-yet-inserted tree, which carries a
        // pending insertion effect. Walk up until we run out of parents.
        let nextNode: Fiber | null = node;
        while (nextNode) {
            node = nextNode;
            if ((node.flags & (PLACEMENT | HYDRATING)) !== 0) {
                // Insertion or in-progress hydration: the nearest possible mounted
                // fiber is the parent, but we keep going to check that one too.
                nearestMounted = node.return;
            }
            nextNode = node.return;
        }
    } else {
        while (node.return) {
            node = node.return;
        }
    }
    // Not landing on the host root means we're in a disconnected, unmounted tree.
    return node.tag === HOST_ROOT ? nearestMounted : null;
}

function assertIsMounted(fiber: Fiber): void {
    if (getNearestMountedFiber(fiber) !== fiber) {
        throw new Error("Unable to find node on an unmounted component.");
    }
}

// A component's fiber pointer may reference either the current tree or the
// work-in-progress alternate, so resolve which branch the root actually points at.
function findCurrentFiberUsingSlowPath(fiber: Fiber): Fiber | null {
    const alternate = fiber.alternate;
    if (!alternate) {
        const nearestMounted = getNearestMountedFiber(fiber);
        if (nearestMounted === null) {
            throw new Error("Unable to find node on an unmounted component.");
        }
        return nearestMounted !== fiber ? null : fiber;
    }
    // Two possible branches: walk back up to the root to see which path it points
    // to, handling the special cases on the way.
    let a = fiber;
    let b = alternate;
    for (;;) {
        const parentA = a.return;
        if (parentA === null) {
            break;
        }
        const parentB = parentA.alternate;
        if (parentB === null) {
            // No alternate. Only happens when a Suspense component is hidden and an
            // extra fragment fiber sits in between: skip over it to the next parent.
            const nextParent = parentA.return;
            if (nextParent !== null) {
                a = nextParent;
                b = nextParent;
                // eslint-disable-next-line no-continue -- kept 1:1 with React's implementation
                continue;
            }
            break;
        }
        if (parentA.child === parentB.child) {
            // Both parent copies point at the same child, so that child is current.
            // Happens when we bail out on low priority and reuse the current child.
            let child = parentA.child;
            while (child) {
                if (child === a) {
                    assertIsMounted(parentA);
                    return fiber;
                }
                if (child === b) {
                    assertIsMounted(parentA);
                    return alternate;
                }
                child = child.sibling;
            }
            // A mounting node never has an alternate, so this can only be an unmount.
            throw new Error("Unable to find node on an unmounted component.");
        }
        if (a.return !== b.return) {
            // Return pointers never criss-cross, so each fiber belongs to the child
            // set of its own parent.
            a = parentA;
            b = parentB;
        } else {
            // Same return pointer: scan both parents' child sets to see which child
            // belongs to which set.
            let didFindChild = false;
            let child = parentA.child;
            while (child) {
                if (child === a) {
                    didFindChild = true;
                    a = parentA;
                    b = parentB;
                    break;
                }
                if (child === b) {
                    didFindChild = true;
                    b = parentA;
                    a = parentB;
                    break;
                }
                child = child.sibling;
            }
            if (!didFindChild) {
                child = parentB.child;
                while (child) {
                    if (child === a) {
                        didFindChild = true;
                        a = parentB;
                        b = parentA;
                        break;
                    }
                    if (child === b) {
                        didFindChild = true;
                        b = parentB;
                        a = parentA;
                        break;
                    }
                    child = child.sibling;
                }
                if (!didFindChild) {
                    throw new Error(
                        "Child was not found in either parent set. This indicates a bug in React related to the return pointer. Please file an issue."
                    );
                }
            }
        }
        if (a.alternate !== b) {
            throw new Error(
                "Return fibers should always be each others' alternates. This error is likely caused by a bug in React. Please file an issue."
            );
        }
    }
    // A root that isn't a host container means we're in a disconnected tree.
    if (a.tag !== HOST_ROOT) {
        throw new Error("Unable to find node on an unmounted component.");
    }
    return (a.stateNode as { current: Fiber }).current === a
        ? fiber
        : alternate;
}

// Drill down to the first host (DOM) fiber in the subtree.
function findCurrentHostFiberImpl(node: Fiber): Fiber | null {
    const tag = node.tag;
    if (
        tag === HOST_COMPONENT ||
        tag === HOST_HOISTABLE ||
        tag === HOST_SINGLETON ||
        tag === HOST_TEXT
    ) {
        return node;
    }
    let child = node.child;
    while (child !== null) {
        const match = findCurrentHostFiberImpl(child);
        if (match !== null) {
            return match;
        }
        child = child.sibling;
    }
    return null;
}

function findCurrentHostFiber(parent: Fiber): Fiber | null {
    const currentParent = findCurrentFiberUsingSlowPath(parent);
    return currentParent !== null
        ? findCurrentHostFiberImpl(currentParent)
        : null;
}

function findHostInstance(component: ReactComponentInternals): unknown {
    const fiber = component._reactInternals;
    if (fiber === undefined) {
        if (typeof (component as { render?: unknown }).render === "function") {
            throw new Error("Unable to find node on an unmounted component.");
        }
        throw new Error(
            `Argument appears to not be a ReactComponent. Keys: ${Object.keys(component).join(",")}`
        );
    }
    const hostFiber = findCurrentHostFiber(fiber);
    return hostFiber === null ? null : hostFiber.stateNode;
}

function findDOMNodeShim(componentOrElement: unknown): unknown {
    if (componentOrElement === null || componentOrElement === undefined) {
        return null;
    }
    if (
        (componentOrElement as { nodeType?: number }).nodeType === ELEMENT_NODE
    ) {
        return componentOrElement;
    }
    return findHostInstance(componentOrElement);
}

const reactDom = ReactDOM as {
    findDOMNode?: typeof findDOMNodeShim;
    createRoot?: typeof createRoot;
};
if (typeof reactDom.findDOMNode !== "function") {
    reactDom.findDOMNode = findDOMNodeShim;
}
if (typeof reactDom.createRoot !== "function") {
    reactDom.createRoot = createRoot;
}
